import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const History = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchHistory();
  }, [filter]);

  const fetchHistory = async () => {
    try {
      const url = filter === 'all' ? `${API}/history` : `${API}/history?status=${filter}`;
      const response = await axios.get(url);
      setHistory(response.data);
    } catch (error) {
      toast.error('Geçmiş yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle size={20} className="text-[#10b981]" />;
      case 'failed':
        return <XCircle size={20} className="text-[#ef4444]" />;
      default:
        return <Clock size={20} className="text-[#f59e0b]" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      sent: 'bg-[#10b981]/10 text-[#10b981]',
      failed: 'bg-[#ef4444]/10 text-[#ef4444]',
      queued: 'bg-[#f59e0b]/10 text-[#f59e0b]'
    };
    const labels = {
      sent: 'Gönderildi',
      failed: 'Başarısız',
      queued: 'Sırada'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] tracking-tight">Geçmiş</h1>
          <p className="text-sm text-[#64748b] mt-1">Tüm e-posta gönderim geçmişini görüntüleyin</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-[#64748b]" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="history-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="sent">Gönderildi</SelectItem>
              <SelectItem value="failed">Başarısız</SelectItem>
              <SelectItem value="queued">Sırada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* History Table */}
      {history.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-[#e2e8f0]">
          <Clock size={64} className="mx-auto text-[#cbd5e1] mb-4" />
          <h3 className="text-lg font-bold text-[#0f172a] mb-2">Henüz geçmiş yok</h3>
          <p className="text-[#64748b]">İlk kampanyanızı gönderin, burada görünsün</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Durum</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Alıcı</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Şablon</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Gönderim Zamanı</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#0f172a]">Mesaj ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {history.map((record) => (
                  <tr key={record.id} className="hover:bg-[#f8fafc] transition-colors" data-testid={`history-row-${record.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(record.status)}
                        {getStatusBadge(record.status)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-[#0f172a]">{record.recipient_email}</p>
                        {record.recipient_name && (
                          <p className="text-xs text-[#64748b]">{record.recipient_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#64748b]">{record.template_name}</td>
                    <td className="px-6 py-4 text-sm text-[#64748b]">
                      {new Date(record.sent_at).toLocaleString('tr-TR')}
                    </td>
                    <td className="px-6 py-4">
                      {record.message_id ? (
                        <code className="text-xs font-mono text-[#64748b] bg-[#f8fafc] px-2 py-1 rounded">
                          {record.message_id.slice(0, 16)}...
                        </code>
                      ) : record.error_message ? (
                        <span className="text-xs text-[#ef4444]">{record.error_message}</span>
                      ) : (
                        <span className="text-xs text-[#cbd5e1]">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
